/**
 * Authority Offline Complaints View
 * Displays and manages offline complaints in table format
 */

import React, { useState } from 'react';
import config from '../../config/environment';
import TablePagination from '../../components/TablePagination';

// Helper function to construct full file URL from relative path
const getFileUrl = (filePath) => {
  if (!filePath) return '';

  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get base URL without /api suffix
  const baseURL = config.api.baseURL.replace('/api', '');

  // Construct full URL - Laravel typically serves files from /storage/
  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

  // Construct URL: baseURL/storage/filePath
  return `${baseURL}/storage/${cleanPath}`;
};

const OfflineComplaintsView = ({
  complaints = [],
  searchTerm = '',
  dateFilter = null,
  onComplaintView,
  columns = null
}) => {
  // Helper function to parse date strings (handles MM/DD/YYYY format)
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0);
    // Try MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length === 3) {
      return new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
    }
    // Fallback to standard date parsing
    return new Date(dateString);
  };

  // Ensure complaints is always an array
  const complaintsArray = Array.isArray(complaints) ? complaints : [];

  // Filter complaints based on search term and date filter
  const filteredComplaints = complaintsArray.filter(complaint => {
    // Search filter
    const matchesSearch = !searchTerm ||
      complaint.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.complaint_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.complaint?.toLowerCase().includes(searchTerm.toLowerCase());

    // Date filter
    let matchesDate = true;
    if (dateFilter && dateFilter.startDate && dateFilter.endDate) {
      try {
        const complaintDate = parseDate(complaint.date);
        const startDate = new Date(dateFilter.startDate);
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999); // Include entire end date
        matchesDate = complaintDate >= startDate && complaintDate <= endDate;
      } catch (error) {
        console.error('Error parsing date:', error);
        matchesDate = true; // If date parsing fails, include the record
      }
    }

    return matchesSearch && matchesDate;
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Pagination Logic
  const totalItems = filteredComplaints.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedComplaints = filteredComplaints.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  // Transform complaints data for DataGrid
  const transformedComplaints = paginatedComplaints.map((complaint, index) => {
    // Use numeric ID for DataGrid - prefer numeric id, fallback to complaint_id string, then index
    let rowId;
    if (complaint.id && typeof complaint.id === 'number') {
      rowId = complaint.id;
    } else if (complaint.complaint_id) {
      rowId = `id-${complaint.complaint_id}`;
    } else {
      rowId = index;
    }

    // Format date from created_at
    let formattedDate = '';
    if (complaint.created_at) {
      try {
        const date = new Date(complaint.created_at);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else {
          formattedDate = complaint.created_at;
        }
      } catch (e) {
        formattedDate = complaint.created_at;
      }
    } else if (complaint.date) {
      formattedDate = complaint.date;
    }

    // Extract user data - check both direct fields and user object
    const userName = complaint.name || (complaint.user && complaint.user.name) || '';
    const userEmail = complaint.email || (complaint.user && complaint.user.email) || '';
    const userMobile = complaint.mobile || (complaint.user && complaint.user.mobile) || '';
    const files = complaint.files || complaint.documents || [];

    // Build the row object with all necessary fields - spread original first, then override
    const rowData = {
      ...complaint,
      id: rowId,
      // Preserve original created_at for valueGetter
      created_at: complaint.created_at || complaint.date || '',
      complaint_id: complaint.complaint_id || String(complaint.id || ''),
      name: userName,
      email: userEmail,
      mobile: userMobile,
      files: files,
      documents: files,
      // Add formatted date for display (used as fallback in valueGetter)
      date: formattedDate,
    };

    return rowData;
  });

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Complaints Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 relative">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Created At</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Complaint ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Complainant Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Phone Number</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Files</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transformedComplaints.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">No records found</td></tr>
              ) : (
                transformedComplaints.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.complaint_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.mobile}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {(() => {
                        const files = row.files || row.documents || [];
                        const complaintId = row.complaint_id || '';

                        if (!Array.isArray(files) || files.length === 0) {
                          return <span className="text-gray-400 text-xs">No files</span>;
                        }

                        return (
                          <div className="flex flex-wrap gap-2">
                            {files.map((file, index) => {
                              const fileType = file.file_type || 'general';
                              const fileId = file.id || '';
                              const chipLabel = `${fileType}-${complaintId}-${fileId}`;
                              const fileUrl = getFileUrl(file.file_path);
                              const isClickable = !!fileUrl;

                              let styles = "bg-gray-100 text-gray-700 border-gray-200";
                              if (fileType === 'supporting') styles = "bg-blue-50 text-blue-700 border-blue-200";

                              return (
                                <span
                                  key={index}
                                  onClick={(e) => {
                                    if (fileUrl) {
                                      window.open(fileUrl, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles} ${isClickable ? 'cursor-pointer hover:bg-opacity-80 transition-opacity' : ''}`}
                                  title={isClickable ? 'Click to view file' : ''}
                                >
                                  {chipLabel}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default OfflineComplaintsView;

